import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, ImagePlus, Loader2, Pause, Play, Share2, Sparkles, X, Maximize2, Minimize2 } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import { Textarea } from '../components/ui/textarea'
import { StepViewer } from '../components/tutor/StepViewer'
import { solveProblem, createShare } from '../api/tutor'
import { getFileUrl, uploadFile } from '../api/qb'
import { SolveResponse } from '../types/tutor'
import { copyToClipboard } from '../lib/utils'

const SPEED_OPTIONS = [
  { label: '0.75x', value: 0.75 },
  { label: '1x', value: 1 },
  { label: '1.5x', value: 1.5 },
  { label: '2x', value: 2 },
]

export default function TutorVisualizationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const [data, setData] = useState<SolveResponse | null>(null)
  const [inputContent, setInputContent] = useState('')
  const [currentStepId, setCurrentStepId] = useState('')
  const [loading, setLoading] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)
  const [imageName, setImageName] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const hasImage = Boolean(imageUrl)
  const hasUsableImage = hasImage && !imageUrl?.startsWith('blob:')
  const canSubmit = Boolean(inputContent.trim()) || hasUsableImage

  const activeData = data
  const currentStepIndex = useMemo(() => {
    if (!activeData) {
      return -1
    }
    return activeData.steps.findIndex((step) => step.step_id === currentStepId)
  }, [activeData, currentStepId])
  const currentStep = useMemo(() => {
    if (!activeData) {
      return null
    }
    if (currentStepIndex < 0) {
      return activeData.steps[0] ?? null
    }
    return activeData.steps[currentStepIndex] ?? null
  }, [activeData, currentStepIndex])

  useEffect(() => {
    const prefillContent = searchParams.get('content')
    const prefillImageUrl = searchParams.get('image_url')
    if (prefillContent) {
      setInputContent(prefillContent)
    }
    if (prefillImageUrl) {
      if (prefillImageUrl.startsWith('blob:')) {
        setImageUrl(null)
        setImageName('')
        toast.error('检测到本地预览链接，请重新上传图片后再解题')
      } else {
        setImageUrl(prefillImageUrl)
        setImageName('已携带图片链接')
      }
    }
  }, [searchParams])

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl)
      }
    }
  }, [imagePreviewUrl])

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  useEffect(() => {
    if (!isPlaying || !activeData || currentStepIndex < 0) {
      return
    }
    const stepDelay = Math.max(700, Math.floor(1800 / playbackSpeed))
    const timer = window.setTimeout(() => {
      if (currentStepIndex >= activeData.steps.length - 1) {
        setIsPlaying(false)
        return
      }
      setCurrentStepId(activeData.steps[currentStepIndex + 1].step_id)
    }, stepDelay)
    return () => window.clearTimeout(timer)
  }, [isPlaying, activeData, currentStepIndex, playbackSpeed])

  const handleSolve = async () => {
    if (!canSubmit) {
      toast.error('请至少输入题目文本或上传图片')
      return
    }
    if (hasImage && !hasUsableImage) {
      toast.error('图片尚未上传完成，请重新上传后再试')
      return
    }
    setLoading(true)
    setIsPlaying(false)
    const normalizedContent = inputContent.trim()
    try {
      const result = await solveProblem({
        content: normalizedContent,
        image_url: hasUsableImage ? imageUrl || undefined : undefined,
      })
      setData(result)
      if (result.steps.length > 0) {
        setCurrentStepId(result.steps[0].step_id)
      }
      toast.success('图示解题已生成')
    } catch (error) {
      console.error(error)
      toast.error('生成失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.currentTarget.value = ''
    if (!file) {
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件')
      return
    }
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    const localPreview = URL.createObjectURL(file)
    setImagePreviewUrl(localPreview)
    setImageName(file.name)
    setUploadingImage(true)
    try {
      const uploaded = await uploadFile(file)
      const resolvedUrl = uploaded.url ? getFileUrl(uploaded.url) : getFileUrl(uploaded.file_id)
      if (resolvedUrl.startsWith('blob:')) {
        throw new Error('图片地址仍是本地预览地址')
      }
      setImageUrl(resolvedUrl)
      toast.success('图片上传成功，已保存为服务器地址')
    } catch (error) {
      console.error(error)
      URL.revokeObjectURL(localPreview)
      setImagePreviewUrl(null)
      setImageName('')
      setImageUrl(null)
      toast.error('图片上传失败')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleRemoveImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl)
    }
    setImagePreviewUrl(null)
    setImageName('')
    setImageUrl(null)
  }

  const handleShare = async () => {
    if (!activeData || !inputContent.trim()) {
      return
    }
    setSharing(true)
    try {
      const share = await createShare({
        input_content: inputContent.trim(),
        result_json: activeData,
      })
      await copyToClipboard(share.share_url)
      toast.success('分享链接已复制')
    } catch (error) {
      console.error(error)
      toast.error('创建分享失败')
    } finally {
      setSharing(false)
    }
  }

  const handlePrev = () => {
    if (!activeData || currentStepIndex <= 0) {
      return
    }
    setCurrentStepId(activeData.steps[currentStepIndex - 1].step_id)
  }

  const handleNext = () => {
    if (!activeData || currentStepIndex >= activeData.steps.length - 1) {
      return
    }
    setCurrentStepId(activeData.steps[currentStepIndex + 1].step_id)
  }

  const handleStepClick = (stepId: string) => {
    if (isPlaying) {
      return
    }
    setCurrentStepId(stepId)
  }

  const handleToggleFullscreen = async () => {
    const target = containerRef.current
    if (!target) {
      return
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }
    await target.requestFullscreen()
  }

  if (!activeData || !currentStep) {
    return (
      <div className="min-h-screen bg-[#0D1117] px-4 py-10 text-slate-100">
        <div className="mx-auto w-full max-w-3xl space-y-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={() => navigate(ROUTES.TUTOR)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回讲解页
            </Button>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
              <Sparkles className="h-3.5 w-3.5" />
              解题过程图示
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#161B22] p-5 shadow-[0_18px_60px_-28px_rgba(0,0,0,0.8)]">
            <div className="space-y-4">
              <Textarea
                placeholder="请输入数学题目，例如：如图，AB∥CD，求∠A与∠D关系。"
                className="min-h-[140px] resize-none border-white/10 bg-[#0D1117] text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500"
                value={inputContent}
                onChange={(event) => setInputContent(event.target.value)}
              />
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-white/15 bg-white/5 text-slate-100 hover:bg-white/10"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={loading || uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      上传中...
                    </>
                  ) : (
                    <>
                      <ImagePlus className="mr-2 h-4 w-4" />
                      上传题目图片
                    </>
                  )}
                </Button>
                {imagePreviewUrl && (
                  <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                    <img src={imagePreviewUrl} alt="题目图片预览" className="max-h-56 w-full rounded-md object-contain" />
                    <div className="flex items-center justify-between text-xs text-slate-300">
                      <span className="truncate">{imageName}</span>
                      <Button type="button" variant="ghost" size="sm" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={handleRemoveImage}>
                        <X className="mr-1 h-3 w-3" />
                        移除
                      </Button>
                    </div>
                    <div className="rounded-md bg-white/[0.04] px-2 py-1 text-[11px] break-all text-slate-300">
                      已保存地址：{imageUrl || '上传处理中...'}
                    </div>
                    {imageUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-slate-200 hover:bg-white/10 hover:text-white"
                        onClick={async () => {
                          const copied = await copyToClipboard(imageUrl)
                          if (copied) {
                            toast.success('图片地址已复制')
                          } else {
                            toast.error('复制失败')
                          }
                        }}
                      >
                        复制图片地址
                      </Button>
                    )}
                  </div>
                )}
                {imageUrl && !imagePreviewUrl && (
                  <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-slate-300">
                    已附带图片链接，提交后将与文本一并发送
                  </div>
                )}
              </div>
              <Button className="h-12 w-full bg-blue-600 text-base hover:bg-blue-500" onClick={handleSolve} disabled={loading || uploadingImage || !canSubmit}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    正在生成图示...
                  </>
                ) : (
                  '生成解题过程图示'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0D1117] text-slate-100">
      <div className="flex h-screen flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 bg-[#111827] px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={() => navigate(ROUTES.TUTOR)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              返回
            </Button>
            <div className="text-sm font-semibold">解题过程图示</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-white/15 bg-white/5 text-slate-200 hover:bg-white/10" onClick={handleShare} disabled={sharing}>
              {sharing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Share2 className="mr-2 h-4 w-4" />}
              分享
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={() => {
              setData(null)
              setIsPlaying(false)
            }}>
              新题目
            </Button>
          </div>
        </header>
        <main className="flex flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="flex min-w-0 flex-1 flex-col lg:w-[64%]">
            <div className="flex-1 overflow-auto p-3 lg:p-5">
              <div className="mx-auto h-full w-full max-w-5xl">
                <div className="h-full rounded-xl border border-white/10 bg-[#161B22] p-3 lg:p-4">
                  <div className="h-full overflow-auto rounded-lg bg-[#0D1117] p-2 lg:p-4">
                    <StepViewer
                      problem={activeData.problem}
                      currentStep={currentStep}
                      historySteps={activeData.steps.slice(0, Math.max(currentStepIndex, 0))}
                      mode={activeData.mode}
                      geometryLesson={activeData.geometry_lesson}
                      className="mx-auto w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex h-16 shrink-0 items-center gap-2 border-t border-white/10 bg-[#161B22] px-3 lg:px-5">
              <Button variant="outline" size="icon" className="border-white/15 bg-white/5 text-slate-100 hover:bg-white/10" onClick={handlePrev} disabled={isPlaying || currentStepIndex <= 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" className="bg-blue-600 text-white hover:bg-blue-500" onClick={() => setIsPlaying((prev) => !prev)}>
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="icon" className="border-white/15 bg-white/5 text-slate-100 hover:bg-white/10" onClick={handleNext} disabled={isPlaying || currentStepIndex >= activeData.steps.length - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <div className="mx-2 h-5 w-px bg-white/10" />
              <div className="text-xs text-slate-300">
                {Math.max(currentStepIndex + 1, 1)} / {activeData.steps.length}
              </div>
              <select
                value={playbackSpeed}
                onChange={(event) => setPlaybackSpeed(Number(event.target.value))}
                className="ml-auto rounded-md border border-white/15 bg-[#0D1117] px-2 py-1 text-xs text-slate-100 outline-none"
              >
                {SPEED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:bg-white/10 hover:text-white" onClick={handleToggleFullscreen}>
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="max-h-[40vh] border-t border-white/10 bg-[#161B22] lg:max-h-none lg:w-[36%] lg:max-w-[420px] lg:border-l lg:border-t-0">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                <div className="text-sm font-medium text-slate-200">步骤导航</div>
                <div className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-slate-300">
                  {Math.max(currentStepIndex + 1, 1)} / {activeData.steps.length}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-3">
                <div className="space-y-2">
                  {activeData.steps.map((step, idx) => {
                    const isActive = step.step_id === currentStep.step_id
                    const isDone = idx < currentStepIndex
                    return (
                      <button
                        key={step.step_id}
                        type="button"
                        disabled={isPlaying}
                        onClick={() => handleStepClick(step.step_id)}
                        className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                          isActive
                            ? 'border-blue-400 bg-blue-500/20'
                            : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                        } ${isPlaying ? 'cursor-not-allowed opacity-70' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                              isActive ? 'bg-blue-500 text-white' : isDone ? 'bg-emerald-500 text-white' : 'bg-white/10 text-slate-200'
                            }`}
                          >
                            {idx + 1}
                          </span>
                          <span className="truncate text-sm text-slate-100">{step.title}</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="border-t border-white/10 px-4 py-3">
                <div className="mb-2 text-xs tracking-wide text-slate-400">当前步骤旁白</div>
                <div className="max-h-24 overflow-y-auto rounded-md bg-white/[0.03] p-3 text-sm leading-relaxed text-slate-200">
                  {currentStep.speak_text || '暂无旁白内容'}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
